<template>
  <div class="gc-wo-bulk-update-step-search">
    <div class="search-page-content">
      <!-- Main column -->
      <div v-show="!showSidebarOnly" class="search-main-col">
        <div class="search-card">
          <div class="search-card-header">
            <span class="search-card-title">Search criteria</span>
          </div>
          <div class="search-card-body">
            <p class="search-hint-text">
              Activate one or more material search types. Active search types
              are combined as a union (OR).
            </p>

            <!-- Material Criterion Blocks -->

            <!-- Block 1: Material Code(s) -->
            <div :class="['stb', { 'stb-on': materialSearch.codeOnly.active }]">
              <div class="stb-hdr">
                <input
                  type="checkbox"
                  v-model="materialSearch.codeOnly.active"
                  class="stb-chk"
                  id="chk-mat-code"
                />
                <label class="stb-lbl" for="chk-mat-code"
                  >Material Code(s)</label
                >
              </div>
              <div class="stb-desc">
                Work orders containing any of the nominated material codes are
                matched.
              </div>
              <div
                v-if="materialSearch.codeOnly.active"
                class="stb-body search-mat-code-block"
              >
                <DxSelectBox
                  ref="codeOnlySelect"
                  :value="codeOnlySelectValue"
                  :data-source="materialCodeDataSource"
                  display-expr="Code"
                  value-expr="Code"
                  :search-enabled="true"
                  :search-expr="['Code', 'Description1']"
                  :drop-down-options="{
                    width: 400,
                  }"
                  item-template="material-item"
                  placeholder="Type or search..."
                  :show-clear-button="false"
                  :disabled="isReadOnly"
                  @focus-in="onMaterialSelectFocusIn"
                  @value-changed="onMaterialCodeSelected('codeOnly', $event)"
                  :width="'250px'"
                >
                  <template #material-item="{ data }">
                    <div class="custom-auto-complete">
                      <span class="auto-complete-primary">{{ data.Code }}</span>
                      <span class="auto-complete-secondary">{{
                        data.Description1
                      }}</span>
                    </div>
                  </template>
                </DxSelectBox>
                <div
                  v-if="materialSearch.codeOnly.codes.length > 0"
                  class="tag-list"
                >
                  <span
                    v-for="(code, ci) in materialSearch.codeOnly.codes"
                    :key="ci"
                    class="stb-tag"
                  >
                    {{ code }}
                    <span class="stb-tag-x" @click="removeCode('codeOnly', ci)"
                      >×</span
                    >
                  </span>
                </div>
                <span
                  v-if="
                    searchAttempted &&
                    materialSearch.codeOnly.codes.length === 0
                  "
                  class="stb-error"
                  >At least one material code is required.</span
                >
              </div>
            </div>

            <!-- Block 2: Material Qty Range -->
            <div :class="['stb', { 'stb-on': materialSearch.qtyRange.active }]">
              <div class="stb-hdr">
                <input
                  type="checkbox"
                  v-model="materialSearch.qtyRange.active"
                  class="stb-chk"
                  id="chk-mat-qty"
                />
                <label class="stb-lbl" for="chk-mat-qty"
                  >Material Qty range</label
                >
              </div>
              <div class="stb-desc">
                Work orders with any material line unit qty within the specified
                range are matched. Quantities are unit quantities — not the
                extended work order qty.
              </div>
              <div v-if="materialSearch.qtyRange.active" class="stb-body">
                <div class="replacement-header">
                  <div class="rt-col-code">Min unit qty</div>
                  <div class="rt-col-code">Max unit qty</div>
                  <div class="rt-col-code">Unit</div>
                  <div class="rt-col-del"></div>
                </div>
                <div
                  v-for="(row, index) in materialSearch.qtyRange.rows"
                  :key="index"
                  class="replacement-row"
                >
                  <div class="rt-field">
                    <DxNumberBox
                      :value="row.minQty"
                      @value-changed="row.minQty = $event.value"
                      :format="'###,###,##0.0000'"
                    />
                    <span
                      v-if="searchAttempted && row.minQty == null"
                      class="param-error"
                      >Required.</span
                    >
                  </div>
                  <div class="rt-field">
                    <DxNumberBox
                      :value="row.maxQty"
                      @value-changed="row.maxQty = $event.value"
                      :format="'###,###,##0.0000'"
                    />
                    <span
                      v-if="searchAttempted && row.maxQty == null"
                      class="param-error"
                      >Required.</span
                    >
                    <span
                      v-else-if="
                        searchAttempted &&
                        row.minQty != null &&
                        row.maxQty < row.minQty
                      "
                      class="param-error"
                      >Must be ≥ Min qty.</span
                    >
                  </div>
                  <div class="rt-field rt-field-narrow">
                    <DxSelectBox
                      :value="row.uom"
                      :data-source="uomDataSource"
                      display-expr="Code"
                      value-expr="Code"
                      :search-enabled="true"
                      placeholder="—"
                      :show-clear-button="true"
                      :drop-down-options="{ width: 120 }"
                      item-template="uom-item"
                      @value-changed="row.uom = $event.value"
                    >
                      <template #uom-item="{ data }">
                        <div class="custom-auto-complete">
                          <span class="auto-complete-primary">{{
                            data.Code
                          }}</span>
                          <span class="auto-complete-secondary">{{
                            data.Description
                          }}</span>
                        </div>
                      </template>
                    </DxSelectBox>
                    <span v-if="searchAttempted && !row.uom" class="param-error"
                      >Required.</span
                    >
                  </div>
                  <div class="rt-col-del">
                    <DxButton
                      icon="trash"
                      type="normal"
                      styling-mode="text"
                      class="rt-del-btn"
                      :disabled="materialSearch.qtyRange.rows.length === 1"
                      @click="removeQtyRangeRow(index)"
                    />
                  </div>
                </div>
                <DxButton
                  text="+ Add Row"
                  type="normal"
                  styling-mode="outlined"
                  class="add-row-btn"
                  @click="addQtyRangeRow"
                />
              </div>
            </div>

            <!-- Block 3: Material Code(s) and Qty -->
            <div
              :class="['stb', { 'stb-on': materialSearch.codeAndQty.active }]"
            >
              <div class="stb-hdr">
                <input
                  type="checkbox"
                  v-model="materialSearch.codeAndQty.active"
                  class="stb-chk"
                  id="chk-mat-both"
                />
                <label class="stb-lbl" for="chk-mat-both"
                  >Material Code(s) and Qty</label
                >
              </div>
              <div class="stb-desc">
                Work orders matched only where nominated code(s) exist with unit
                qty within the specified range. Quantities are unit quantities —
                not the extended work order qty.
              </div>
              <div v-if="materialSearch.codeAndQty.active" class="stb-body">
                <DxSelectBox
                  ref="codeAndQtySelect"
                  :value="codeAndQtySelectValue"
                  :data-source="materialCodeDataSource"
                  display-expr="Code"
                  value-expr="Code"
                  :search-enabled="true"
                  :search-expr="['Code', 'Description1']"
                  :drop-down-options="{ width: 400 }"
                  item-template="material-item"
                  placeholder="Type or search..."
                  :show-clear-button="false"
                  :disabled="isReadOnly"
                  style="margin-bottom: 8px"
                  @focus-in="onMaterialSelectFocusIn"
                  @value-changed="onMaterialCodeSelected('codeAndQty', $event)"
                  :width="'250px'"
                >
                  <template #material-item="{ data }">
                    <div class="custom-auto-complete">
                      <span class="auto-complete-primary">{{ data.Code }}</span>
                      <span class="auto-complete-secondary">{{
                        data.Description1
                      }}</span>
                    </div>
                  </template>
                </DxSelectBox>
                <div
                  v-if="materialSearch.codeAndQty.codes.length > 0"
                  class="tag-list"
                  style="margin-bottom: 8px"
                >
                  <span
                    v-for="(code, ci) in materialSearch.codeAndQty.codes"
                    :key="ci"
                    class="stb-tag"
                  >
                    {{ code }}
                    <span
                      class="stb-tag-x"
                      @click="removeCode('codeAndQty', ci)"
                      >×</span
                    >
                  </span>
                </div>
                <span
                  v-if="
                    searchAttempted &&
                    materialSearch.codeAndQty.codes.length === 0
                  "
                  class="stb-error"
                  style="display: block; margin-bottom: 6px"
                  >At least one material code is required.</span
                >
                <div class="replacement-header">
                  <div class="rt-col-code">Min unit qty</div>
                  <div class="rt-col-code">Max unit qty</div>
                  <div class="rt-col-code">Unit</div>
                  <div class="rt-col-del"></div>
                </div>
                <div
                  v-for="(row, index) in materialSearch.codeAndQty.rows"
                  :key="index"
                  class="replacement-row"
                >
                  <div class="rt-field">
                    <DxNumberBox
                      :value="row.minQty"
                      @value-changed="row.minQty = $event.value"
                      :format="'###,###,##0.0000'"
                    />
                    <span
                      v-if="searchAttempted && row.minQty == null"
                      class="param-error"
                      >Required.</span
                    >
                  </div>
                  <div class="rt-field">
                    <DxNumberBox
                      :value="row.maxQty"
                      @value-changed="row.maxQty = $event.value"
                      :format="'###,###,##0.0000'"
                    />
                    <span
                      v-if="searchAttempted && row.maxQty == null"
                      class="param-error"
                      >Required.</span
                    >
                    <span
                      v-else-if="
                        searchAttempted &&
                        row.minQty != null &&
                        row.maxQty < row.minQty
                      "
                      class="param-error"
                      >Must be ≥ Min qty.</span
                    >
                  </div>
                  <div class="rt-field rt-field-narrow">
                    <DxSelectBox
                      :value="row.uom"
                      :data-source="uomDataSource"
                      display-expr="Code"
                      value-expr="Code"
                      :search-enabled="true"
                      placeholder="—"
                      :show-clear-button="true"
                      :drop-down-options="{ width: 120 }"
                      item-template="uom-item"
                      @value-changed="row.uom = $event.value"
                    >
                      <template #uom-item="{ data }">
                        <div class="custom-auto-complete">
                          <span class="auto-complete-primary">{{
                            data.Code
                          }}</span>
                          <span class="auto-complete-secondary">{{
                            data.Description
                          }}</span>
                        </div>
                      </template>
                    </DxSelectBox>
                    <span v-if="searchAttempted && !row.uom" class="param-error"
                      >Required.</span
                    >
                  </div>
                  <div class="rt-col-del">
                    <DxButton
                      icon="trash"
                      type="normal"
                      styling-mode="text"
                      class="rt-del-btn"
                      :disabled="materialSearch.codeAndQty.rows.length === 1"
                      @click="removeCodeAndQtyRow(index)"
                    />
                  </div>
                </div>
                <DxButton
                  text="+ Add Row"
                  type="normal"
                  styling-mode="outlined"
                  class="add-row-btn"
                  @click="addCodeAndQtyRow"
                />
              </div>
            </div>

            <!-- Block 4: Material Attributes -->
            <div
              :class="[
                'stb',
                { 'stb-on': materialSearch.materialAttributes.active },
              ]"
            >
              <div class="stb-hdr">
                <input
                  type="checkbox"
                  v-model="materialSearch.materialAttributes.active"
                  class="stb-chk"
                  id="chk-mat-attr"
                />
                <label class="stb-lbl" for="chk-mat-attr"
                  >Material Attributes</label
                >
              </div>
              <div class="stb-desc">
                Matches work orders containing at least one material whose
                product attributes match the selected values. Multiple values
                within a field are combined as OR; multiple fields are combined
                as AND.
              </div>
              <div
                v-if="materialSearch.materialAttributes.active"
                class="stb-body"
              >
                <div class="attribute-grid">
                  <div class="attribute-field">
                    <label>Product Category 1</label>
                    <DxTagBox
                      :maxDisplayedTags="2"
                      :showMultiTagOnly="false"
                      :value="
                        materialSearch.materialAttributes.productCategory1Codes
                      "
                      :data-source="
                        materialAttributesDataSources.productCategory1
                      "
                      value-expr="Code"
                      :display-expr="
                        (e) => {
                          if (e) return `${e.Code} - ${e.Description}`;
                          return '';
                        }
                      "
                      :search-enabled="true"
                      :search-expr="['Code', 'Description']"
                      :show-clear-button="true"
                      placeholder="Select..."
                      :disabled="isReadOnly"
                      @value-changed="
                        materialSearch.materialAttributes.productCategory1Codes =
                          $event.value
                      "
                      :item-template="
                        (data) => {
                          return `<div class='custom-auto-complete'>
                          <span class='auto-complete-primary'>${data.Description}</span>
                          <span class='auto-complete-secondary'>${data.Code}</span>
                        </div>`;
                        }
                      "
                    >
                    </DxTagBox>
                  </div>

                  <div class="attribute-field">
                    <label>Product Category 2</label>
                    <DxTagBox
                      :maxDisplayedTags="2"
                      :showMultiTagOnly="false"
                      :value="
                        materialSearch.materialAttributes.productCategory2Codes
                      "
                      :data-source="
                        materialAttributesDataSources.productCategory2
                      "
                      value-expr="Code"
                      :display-expr="
                        (e) => {
                          if (e) return `${e.Code} - ${e.Description}`;
                          return '';
                        }
                      "
                      :search-enabled="true"
                      :search-expr="['Code', 'Description']"
                      :show-clear-button="true"
                      placeholder="Select..."
                      :disabled="isReadOnly"
                      @value-changed="
                        materialSearch.materialAttributes.productCategory2Codes =
                          $event.value
                      "
                      :item-template="
                        (data) => {
                          return `<div class='custom-auto-complete'>
                            <span class='auto-complete-primary'>${data.Description}</span>
                            <span class='auto-complete-secondary'>${data.Code}</span>
                          </div>`;
                        }
                      "
                    >
                    </DxTagBox>
                  </div>

                  <div class="attribute-field">
                    <label>Product Category 3</label>
                    <DxTagBox
                      :maxDisplayedTags="2"
                      :showMultiTagOnly="false"
                      :value="
                        materialSearch.materialAttributes.productCategory3Codes
                      "
                      :data-source="
                        materialAttributesDataSources.productCategory3
                      "
                      value-expr="Code"
                      :display-expr="
                        (e) => {
                          if (e) return `${e.Code} - ${e.Description}`;
                          return '';
                        }
                      "
                      :search-enabled="true"
                      :search-expr="['Code', 'Description']"
                      :show-clear-button="true"
                      placeholder="Select..."
                      :disabled="isReadOnly"
                      @value-changed="
                        materialSearch.materialAttributes.productCategory3Codes =
                          $event.value
                      "
                      :item-template="
                        (data) => {
                          return `<div class='custom-auto-complete'>
                            <span class='auto-complete-primary'>${data.Description}</span>
                            <span class='auto-complete-secondary'>${data.Code}</span>
                          </div>`;
                        }
                      "
                    >
                    </DxTagBox>
                  </div>

                  <div class="attribute-field">
                    <label>Product Category 4</label>
                    <DxTagBox
                      :maxDisplayedTags="2"
                      :showMultiTagOnly="false"
                      :value="
                        materialSearch.materialAttributes.productCategory4Codes
                      "
                      :data-source="
                        materialAttributesDataSources.productCategory4
                      "
                      value-expr="Code"
                      :display-expr="
                        (e) => {
                          if (e) return `${e.Code} - ${e.Description}`;
                          return '';
                        }
                      "
                      :search-enabled="true"
                      :search-expr="['Code', 'Description']"
                      :show-clear-button="true"
                      placeholder="Select..."
                      :disabled="isReadOnly"
                      @value-changed="
                        materialSearch.materialAttributes.productCategory4Codes =
                          $event.value
                      "
                      :item-template="
                        (data) => {
                          return `<div class='custom-auto-complete'>
                            <span class='auto-complete-primary'>${data.Description}</span>
                            <span class='auto-complete-secondary'>${data.Code}</span>
                          </div>`;
                        }
                      "
                    >
                    </DxTagBox>
                  </div>

                  <div class="attribute-field">
                    <label>Product Type</label>
                    <DxTagBox
                      :maxDisplayedTags="2"
                      :showMultiTagOnly="false"
                      :value="
                        materialSearch.materialAttributes.productTypeCodes
                      "
                      :data-source="materialAttributesDataSources.productType"
                      value-expr="Code"
                      :display-expr="
                        (e) => {
                          if (e) return `${e.Code} - ${e.Description}`;
                          return '';
                        }
                      "
                      :search-enabled="true"
                      :search-expr="['Code', 'Description']"
                      :show-clear-button="true"
                      placeholder="Select..."
                      :disabled="isReadOnly"
                      @value-changed="
                        materialSearch.materialAttributes.productTypeCodes =
                          $event.value
                      "
                      :item-template="
                        (data) => {
                          return `<div class='custom-auto-complete'>
                            <span class='auto-complete-primary'>${data.Description}</span>
                            <span class='auto-complete-secondary'>${data.Code}</span>
                          </div>`;
                        }
                      "
                    >
                    </DxTagBox>
                  </div>
                </div>
              </div>
            </div>

            <div class="search-divider" />

            <!-- WO Scope Filters -->
            <div class="scope-section-hdr">
              Additional Work Order filters (optional)
            </div>
            <div
              :class="[
                'scope-form-wrap',
                { 'scope-form-readonly': isReadOnly },
              ]"
            >
              <gc-form
                form-key="scope-main"
                :form-items="scopeFormItems"
                :form-options="scopeFormOptions"
                :is-normal="true"
                :data-model="scopeFilters"
                :field-data-changed="onScopeFieldChanged"
                form-location="form-normal"
                :scrolling-enabled="false"
                validation-group="scope-main-form"
                :formMode="baseView.mode"
              />
            </div>

            <!-- Product Attributes (1–20) — collapsible -->
            <div
              class="scope-attrs-toggle"
              @click="attributesPanelExpanded = !attributesPanelExpanded"
            >
              <span class="scope-section-hdr">Product Attributes (1–20)</span>
              <span v-if="activeAttributeCount > 0" class="scope-attrs-badge">{{
                activeAttributeCount
              }}</span>
              <span class="scope-attrs-caret">{{
                attributesPanelExpanded ? "▲" : "▼"
              }}</span>
            </div>
            <div v-show="attributesPanelExpanded" class="scope-attrs-grid">
              <div v-for="n in 20" :key="n" class="scope-attr-field">
                <span class="stb-field-label"
                  >Attr {{ String(n).padStart(2, "0") }}</span
                >
                <DxSelectBox
                  :value="
                    attrScopeFilters[
                      'productAttribute' + String(n).padStart(2, '0')
                    ]
                  "
                  :data-source="attrDataSources[n - 1]"
                  value-expr="Code"
                  :display-expr="
                    (e) => {
                      if (e) return `${e.Code} - ${e.Description}`;
                      return '';
                    }
                  "
                  :search-enabled="true"
                  :search-expr="['Code', 'Description']"
                  :show-clear-button="true"
                  placeholder="All"
                  :disabled="isReadOnly"
                  @value-changed="
                    onAttrValueChanged(
                      'productAttribute' + String(n).padStart(2, '0'),
                      $event,
                    )
                  "
                  @selection-changed="
                    onAttrSelectionChanged(
                      'productAttribute' + String(n).padStart(2, '0'),
                      $event,
                    )
                  "
                  :item-template="
                    (data) => {
                      return `<div class='custom-auto-complete'>
                        <span class='auto-complete-primary'>${data.Description}</span>
                        <span class='auto-complete-secondary'>${data.Code}</span>
                      </div>`;
                    }
                  "
                >
                </DxSelectBox>
              </div>
            </div>

            <!-- Search button -->
            <div class="search-action-row">
              <span
                v-if="searchAttempted && !hasMaterialCriteria"
                class="search-error-hint"
                >Please activate at least one search type.</span
              >
              <DxButton
                :text="searching ? 'Searching...' : 'Search Work Orders →'"
                type="default"
                styling-mode="contained"
                :disabled="!canSearch || searching || isReadOnly"
                @click="onSearch"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Active Filters Sidebar -->
      <div class="search-filter-col">
        <div :class="['search-card', { 'filters-readonly': showSidebarOnly }]">
          <div class="search-card-header">
            <span class="search-card-title">Active filters</span>
            <DxButton
              v-if="hasAnyActiveFilters && !showSidebarOnly"
              text="Reset"
              type="normal"
              styling-mode="outlined"
              @click="resetStep1Filters"
            />
          </div>
          <div class="search-card-body">
            <template v-if="hasAnyActiveFilters">
              <!-- Material Codes -->
              <div
                v-if="
                  materialSearch.codeOnly.active &&
                  materialSearch.codeOnly.codes.length > 0
                "
              >
                <span class="filter-section-label">Material codes</span>
                <div>
                  <span
                    v-for="(code, idx) in materialSearch.codeOnly.codes"
                    :key="idx"
                    class="filter-chip"
                  >
                    {{ code }}
                    <span class="chip-x" @click="removeCode('codeOnly', idx)"
                      >×</span
                    >
                  </span>
                </div>
              </div>

              <!-- Qty Range -->
              <div
                v-if="
                  materialSearch.qtyRange.active &&
                  materialSearch.qtyRange.rows.length > 0
                "
              >
                <span class="filter-section-label">Qty Range</span>
                <div>
                  <span
                    v-for="(row, idx) in materialSearch.qtyRange.rows"
                    :key="idx"
                    class="filter-chip"
                  >
                    {{ row.minQty }} – {{ row.maxQty }} {{ row.uom }}
                    <span class="chip-x" @click="removeQtyRangeRow(idx)"
                      >×</span
                    >
                  </span>
                </div>
              </div>

              <!-- Code & Qty -->
              <div
                v-if="
                  materialSearch.codeAndQty.active &&
                  materialSearch.codeAndQty.codes.length > 0
                "
              >
                <span class="filter-section-label">Code & Qty</span>
                <div>
                  <span
                    v-for="(code, idx) in materialSearch.codeAndQty.codes"
                    :key="idx"
                    class="filter-chip"
                  >
                    {{ code }}
                    <span class="chip-x" @click="removeCode('codeAndQty', idx)"
                      >×</span
                    >
                  </span>
                </div>
              </div>

              <!-- Material Attributes -->
              <div v-if="materialSearch.materialAttributes.active">
                <span class="filter-section-label">Material Attributes</span>
              </div>

              <!-- Work Order Type -->
              <template v-if="scopeFilters.woType != null">
                <span class="filter-section-label">Work Order Type</span>
                <div>
                  <span class="filter-chip">
                    {{ scopeFilters.woType }}
                    <span class="chip-x" @click="scopeFilters.woType = null"
                      >×</span
                    >
                  </span>
                </div>
              </template>

              <!-- Work Order Status -->
              <template v-if="scopeFilters.woStatus != null">
                <span class="filter-section-label">WO Status</span>
                <div>
                  <span class="filter-chip">
                    {{ scopeFilters.woStatus }}
                    <span class="chip-x" @click="scopeFilters.woStatus = null"
                      >×</span
                    >
                  </span>
                </div>
              </template>

              <!-- Branch -->
              <template v-if="scopeFilters.branchCode != null">
                <span class="filter-section-label">Branch</span>
                <div>
                  <span class="filter-chip">
                    {{ scopeFilters.branchCode }}
                    <span class="chip-x" @click="scopeFilters.branchCode = null"
                      >×</span
                    >
                  </span>
                </div>
              </template>

              <!-- Division -->
              <template v-if="scopeFilters.divisionCode != null">
                <span class="filter-section-label">Division</span>
                <div>
                  <span class="filter-chip">
                    {{ scopeFilters.divisionCode }}
                    <span
                      class="chip-x"
                      @click="scopeFilters.divisionCode = null"
                      >×</span
                    >
                  </span>
                </div>
              </template>

              <!-- Scheduled Date -->
              <div
                v-if="
                  scopeFilters.scheduledDateFrom || scopeFilters.scheduledDateTo
                "
              >
                <span class="filter-section-label">Scheduled Date</span>
                <div>
                  <span class="filter-chip">
                    {{
                      formatDateRange(
                        scopeFilters.scheduledDateFrom,
                        scopeFilters.scheduledDateTo,
                      )
                    }}
                  </span>
                </div>
              </div>

              <!-- Product -->
              <template v-if="scopeFilters.productCode != null">
                <span class="filter-section-label">Product</span>
                <div>
                  <span class="filter-chip">
                    {{ scopeFilters.productCode }}
                    <span
                      class="chip-x"
                      @click="scopeFilters.productCode = null"
                      >×</span
                    >
                  </span>
                </div>
              </template>

              <!-- Product Type -->
              <template v-if="scopeFilters.productType != null">
                <span class="filter-section-label">Product Type</span>
                <div>
                  <span class="filter-chip">
                    {{ scopeFilters.productType }}
                    <span
                      class="chip-x"
                      @click="scopeFilters.productType = null"
                      >×</span
                    >
                  </span>
                </div>
              </template>

              <!-- Product Categories -->
              <template
                v-if="
                  scopeFilters.productCategory1 != null ||
                  scopeFilters.productCategory2 != null ||
                  scopeFilters.productCategory3 != null ||
                  scopeFilters.productCategory4 != null
                "
              >
                <span class="filter-section-label">Product Category</span>
                <div>
                  <span
                    v-if="scopeFilters.productCategory1 != null"
                    class="filter-chip"
                  >
                    Cat 1: {{ scopeFilters.productCategory1 }}
                    <span
                      class="chip-x"
                      @click="scopeFilters.productCategory1 = null"
                      >×</span
                    >
                  </span>
                  <span
                    v-if="scopeFilters.productCategory2 != null"
                    class="filter-chip"
                  >
                    Cat 2: {{ scopeFilters.productCategory2 }}
                    <span
                      class="chip-x"
                      @click="scopeFilters.productCategory2 = null"
                      >×</span
                    >
                  </span>
                  <span
                    v-if="scopeFilters.productCategory3 != null"
                    class="filter-chip"
                  >
                    Cat 3: {{ scopeFilters.productCategory3 }}
                    <span
                      class="chip-x"
                      @click="scopeFilters.productCategory3 = null"
                      >×</span
                    >
                  </span>
                  <span
                    v-if="scopeFilters.productCategory4 != null"
                    class="filter-chip"
                  >
                    Cat 4: {{ scopeFilters.productCategory4 }}
                    <span
                      class="chip-x"
                      @click="scopeFilters.productCategory4 = null"
                      >×</span
                    >
                  </span>
                </div>
              </template>
            </template>
            <p v-else class="search-no-filters">No active filters.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DxSelectBox from "devextreme-vue/select-box";
import DxTagBox from "devextreme-vue/tag-box";
import DxNumberBox from "devextreme-vue/number-box";
import DxButton from "devextreme-vue/button";
import ApiService from "@/_services/api/api.service";
import GcForm from "@/components/gc-form";
import { WoBulkUpdateService } from "@/_services/api/production/wo-bulk-update.service";

export default {
  name: "gc-wo-bulk-update-step-search",
  components: {
    DxSelectBox,
    DxTagBox,
    DxNumberBox,
    DxButton,
    GcForm,
  },
  props: {
    isReadOnly: {
      type: Boolean,
      default: false,
    },
    savedCriteria: {
      type: Object,
      default: () => ({}),
    },
    baseView: {
      type: Object,
      default: () => ({}),
    },
    showLoading: {
      type: Function,
      required: true,
    },
    showSidebarOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchAttempted: false,
      searching: false,
      lastSearchedSnapshot: null,
      initialRestoreDone: false,
      materialCodeDataSource: null,
      uomDataSource: null,
      woStatusDataSource: [
        { Code: "D", Description: "Draft" },
        { Code: "P", Description: "Planning" },
        { Code: "A", Description: "Awaiting Production" },
        { Code: "I", Description: "In Production" },
      ],
      branchDataSource: null,
      divisionDataSource: null,
      productDataSource: null,
      materialSearch: {
        codeOnly: {
          active: false,
          codes: [],
        },
        qtyRange: {
          active: false,
          rows: [{ minQty: null, maxQty: null, uom: null }],
        },
        codeAndQty: {
          active: false,
          codes: [],
          rows: [{ minQty: null, maxQty: null, uom: null }],
        },
        materialAttributes: {
          active: false,
          productCategory1Codes: [],
          productCategory2Codes: [],
          productCategory3Codes: [],
          productCategory4Codes: [],
          productTypeCodes: [],
        },
      },
      scopeFilters: {
        woStatus: null,
        woType: null,
        branchCode: null,
        divisionCode: null,
        scheduledDateFrom: null,
        scheduledDateTo: null,
        productCode: null,
        productCategory1: null,
        productCategory2: null,
        productCategory3: null,
        productCategory4: null,
        productType: null,
      },
      codeOnlySelectValue: null,
      codeAndQtySelectValue: null,
      attributesPanelExpanded: false,
      scopeFormOptions: { gcLabelLocation: "top" },
      attrScopeFilters: (() => {
        const obj = {};
        for (let i = 1; i <= 20; i++)
          obj[`productAttribute${String(i).padStart(2, "0")}`] = null;
        return obj;
      })(),
      attrGuidMap: (() => {
        const obj = {};
        for (let i = 1; i <= 20; i++)
          obj[`productAttribute${String(i).padStart(2, "0")}`] = null;
        return obj;
      })(),
    };
  },
  watch: {
    "materialSearch.codeOnly.active"(newVal) {
      if (!newVal) {
        this.materialSearch.codeOnly.codes = [];
      }
    },
    "materialSearch.qtyRange.active"(newVal) {
      if (!newVal) {
        this.materialSearch.qtyRange.rows = [
          { minQty: null, maxQty: null, uom: null },
        ];
      }
    },
    "materialSearch.codeAndQty.active"(newVal) {
      if (!newVal) {
        this.materialSearch.codeAndQty.codes = [];
        this.materialSearch.codeAndQty.rows = [
          { minQty: null, maxQty: null, uom: null },
        ];
      }
    },
    "materialSearch.materialAttributes.active"(newVal) {
      if (!newVal) {
        this.materialSearch.materialAttributes.productCategory1Codes = [];
        this.materialSearch.materialAttributes.productCategory2Codes = [];
        this.materialSearch.materialAttributes.productCategory3Codes = [];
        this.materialSearch.materialAttributes.productCategory4Codes = [];
        this.materialSearch.materialAttributes.productTypeCodes = [];
      }
    },
    savedCriteria(newVal) {
      if (this.initialRestoreDone) return;
      this.updateOnSavedCriteria(newVal);
    },
  },
  computed: {
    activeAttributeCount() {
      return Object.values(this.attrScopeFilters).filter((v) => v != null)
        .length;
    },
    attrDataSources() {
      return Array.from({ length: 20 }, (_, i) => {
        const num = String(i + 1).padStart(2, "0");
        return ApiService.createDataSource(`api/ProductAttribute${num}`, {
          select: ["RecordId", "Code", "Description"],
          sort: "Code asc",
          key: ["RecordId"],
          keyType: { RecordId: "Guid" },
          fieldTypes: { RecordId: "Guid" },
        });
      });
    },
    hasMaterialCriteria() {
      return (
        this.materialSearch.codeOnly.active ||
        this.materialSearch.qtyRange.active ||
        this.materialSearch.codeAndQty.active ||
        this.materialSearch.materialAttributes.active
      );
    },
    canSearch() {
      return this.hasMaterialCriteria && this.isValidSearch();
    },
    hasChangedSinceSearch() {
      if (!this.lastSearchedSnapshot) return false;
      return (
        JSON.stringify(this.buildSearchPayload().SearchCriteria) !==
        this.lastSearchedSnapshot
      );
    },
    hasAnyActiveFilters() {
      return (
        (this.materialSearch.codeOnly.codes &&
          this.materialSearch.codeOnly.codes.length > 0) ||
        (this.materialSearch.qtyRange.rows &&
          this.materialSearch.qtyRange.rows.length > 0) ||
        (this.materialSearch.codeAndQty.codes &&
          this.materialSearch.codeAndQty.codes.length > 0) ||
        this.materialSearch.materialAttributes.active ||
        this.scopeFilters.woStatus !== null ||
        this.scopeFilters.woType !== null ||
        this.scopeFilters.branchCode !== null ||
        this.scopeFilters.divisionCode !== null ||
        this.scopeFilters.scheduledDateFrom ||
        this.scopeFilters.scheduledDateTo ||
        this.scopeFilters.productCode !== null ||
        this.scopeFilters.productCategory1 !== null ||
        this.scopeFilters.productCategory2 !== null ||
        this.scopeFilters.productCategory3 !== null ||
        this.scopeFilters.productCategory4 !== null ||
        this.scopeFilters.productType !== null
      );
    },
    materialAttributesDataSources() {
      return {
        productCategory1: ApiService.createDataSource("api/ProductCategory1", {
          select: ["RecordId", "Code", "Description", "Inactive"],
          sort: "Code asc",
          key: ["RecordId"],
          keyType: { RecordId: "Guid" },
          fieldTypes: { RecordId: "Guid" },
          filter: ["Inactive", "=", "N"],
        }),
        productCategory2: ApiService.createDataSource("api/ProductCategory2", {
          select: ["RecordId", "Code", "Description", "Inactive"],
          sort: "Code asc",
          key: ["RecordId"],
          keyType: { RecordId: "Guid" },
          fieldTypes: { RecordId: "Guid" },
          filter: ["Inactive", "=", "N"],
        }),
        productCategory3: ApiService.createDataSource("api/ProductCategory3", {
          select: ["RecordId", "Code", "Description", "Inactive"],
          sort: "Code asc",
          key: ["RecordId"],
          keyType: { RecordId: "Guid" },
          fieldTypes: { RecordId: "Guid" },
          filter: ["Inactive", "=", "N"],
        }),
        productCategory4: ApiService.createDataSource("api/ProductCategory4", {
          select: ["RecordId", "Code", "Description", "Inactive"],
          sort: "Code asc",
          key: ["RecordId"],
          keyType: { RecordId: "Guid" },
          fieldTypes: { RecordId: "Guid" },
          filter: ["Inactive", "=", "N"],
        }),
        productType: ApiService.createDataSource("api/ProductType", {
          select: ["RecordId", "Code", "Description", "Inactive"],
          sort: "Code asc",
          key: ["RecordId"],
          keyType: { RecordId: "Guid" },
          fieldTypes: { RecordId: "Guid" },
          filter: ["Inactive", "=", "N"],
        }),
      };
    },
    scopeFormItems() {
      return [
        {
          itemType: "group",
          colCount: 4,
          items: [
            {
              dataField: "woType",
              label: { text: "Work Order Type" },
              editorType: "dxSelectBox",
              editorOptions: {
                dataSource: ["Production", "Maintenance"],
                showClearButton: true,
                placeholder: "All",
              },
            },
            {
              dataField: "woStatus",
              label: { text: "WO Status" },
              editorType: "dxSelectBox",
              editorOptions: {
                dataSource: this.woStatusDataSource,
                valueExpr: "Code",
                displayExpr: (e) => {
                  if (e) return e.FriendlyStatus;
                  else undefined;
                },
                searchExpr: ["Code", "FriendlyStatus"],
                showClearButton: true,
                placeholder: "All",
              },
            },
            {
              screenField: "Branch",
              label: { text: "Branch" },
              dataField: "branchCode",
              editorOptions: { placeholder: "All", showClearButton: true },
              displayExpr: (e) => {
                if (e) return `${e.Code} - ${e.Description}`;
                else undefined;
              },
              searchExpr: ["Code", "Description"],
            },
            {
              screenField: "Division",
              label: { text: "Division" },
              dataField: "divisionCode",
              editorOptions: { placeholder: "All", showClearButton: true },
              displayExpr: (e) => {
                if (e) return `${e.Code} - ${e.Description}`;
                else undefined;
              },
              searchExpr: ["Code", "Description"],
            },
            {
              dataField: "scheduledDateFrom",
              label: { text: "Scheduled Date From" },
              editorType: "dxDateBox",
              editorOptions: {
                placeholder: "All",
                displayFormat: "dd MMM yyyy",
              },
            },
            {
              dataField: "scheduledDateTo",
              label: { text: "Scheduled Date To" },
              editorType: "dxDateBox",
              editorOptions: {
                placeholder: "All",
                displayFormat: "dd MMM yyyy",
              },
            },
            {
              screenField: "Product",
              label: { text: "Product" },
              dataField: "productCode",
              editorOptions: { placeholder: "All", showClearButton: true },
              displayExpr: (e) => {
                if (e) return `${e.Code} - ${e.Description}`;
                else undefined;
              },
              searchExpr: ["Code", "Description1"],
            },
            {
              screenField: "ProductCategory1",
              dataField: "productCategory1",
              editorOptions: { placeholder: "All", showClearButton: true },
              displayExpr: (e) => {
                if (e) return `${e.Code} - ${e.Description}`;
                else undefined;
              },
              searchExpr: ["Code", "Description"],
            },
            {
              screenField: "ProductCategory2",
              dataField: "productCategory2",
              editorOptions: { placeholder: "All", showClearButton: true },
              displayExpr: (e) => {
                if (e) return `${e.Code} - ${e.Description}`;
                else undefined;
              },
              searchExpr: ["Code", "Description"],
            },
            {
              screenField: "ProductCategory3",
              dataField: "productCategory3",
              editorOptions: { placeholder: "All", showClearButton: true },
              displayExpr: (e) => {
                if (e) return `${e.Code} - ${e.Description}`;
                else undefined;
              },
              searchExpr: ["Code", "Description"],
            },
            {
              screenField: "ProductCategory4",
              dataField: "productCategory4",
              editorOptions: { placeholder: "All", showClearButton: true },
              displayExpr: (e) => {
                if (e) return `${e.Code} - ${e.Description}`;
                else undefined;
              },
              searchExpr: ["Code", "Description"],
            },
            {
              screenField: "ProductType",
              dataField: "productType",
              editorOptions: { placeholder: "All", showClearButton: true },
              displayExpr: (e) => {
                if (e) return `${e.Code} - ${e.Description}`;
                else undefined;
              },
              searchExpr: ["Code", "Description"],
            },
          ],
        },
      ];
    },
  },
  created() {
    // Initialize data sources - same pattern as BOM bulk update
    this.materialCodeDataSource = ApiService.createDataSource("api/Product", {
      select: ["Code", "Description1", "Inactive", "Status"],
      filter: [["Inactive", "=", "N"], "and", ["Status", "=", "A"]],
    });
    this.uomDataSource = ApiService.createDataSource("api/UOM", {
      select: ["Code", "IsoCode", "Description", "Inactive"],
      filter: ["Inactive", "=", "N"],
    });
    this.woStatusDataSource = ApiService.createDataSource(
      "api/WorkOrderStatus",
      {
        select: ["Code", "FriendlyStatus", "Inactive"],
        filter: [
          ["Inactive", "=", "N"],
          "and",
          [
            ["Code", "=", "D"],
            "or",
            ["Code", "=", "P"],
            "or",
            ["Code", "=", "A"],
            "or",
            ["Code", "=", "I"],
          ],
        ],
        sort: "Sequence asc",
      },
    );
    this.branchDataSource = ApiService.createDataSource("api/Branch", {
      select: ["Code", "Description"],
    });
    this.divisionDataSource = ApiService.createDataSource("api/Division", {
      select: ["Code", "Description"],
    });
    this.productDataSource = ApiService.createDataSource("api/Product", {
      select: ["Code", "Description1"],
    });

    if (this.savedCriteria) {
      this.updateOnSavedCriteria(this.savedCriteria);
    }
  },
  methods: {
    updateOnSavedCriteria(newVal) {
      const matCriteria =
        (newVal && (newVal.MaterialCriteria || newVal.materialCriteria)) || [];
      if (matCriteria.length > 0) {
        this.restoreFromCriteria(newVal);
        this.initialRestoreDone = true;
      }
    },

    async restoreFromCriteria(criteria) {
      const matCriteria =
        criteria.MaterialCriteria || criteria.materialCriteria || [];
      const scope = criteria.ScopeFilters || criteria.scopeFilters || {};

      var qtyRangeRows = [];
      var codeAndQtyRows = [];
      var codeAndQtyCodes = [];

      for (var i = 0; i < matCriteria.length; i++) {
        var mc = matCriteria[i];
        var type = mc.SearchType || mc.searchType;

        if (type === "CodeOnly") {
          this.materialSearch.codeOnly.active = true;
          this.materialSearch.codeOnly.codes =
            mc.MaterialCodes || mc.materialCodes || [];
        } else if (type === "QtyRangeOnly") {
          this.materialSearch.qtyRange.active = true;
          qtyRangeRows.push({
            minQty: mc.MinQty != null ? mc.MinQty : mc.minQty,
            maxQty: mc.MaxQty != null ? mc.MaxQty : mc.maxQty,
            uom: mc.UnitOfMeasure || mc.unitOfMeasure || null,
          });
        } else if (type === "CodeAndQty") {
          this.materialSearch.codeAndQty.active = true;
          if (codeAndQtyCodes.length === 0) {
            codeAndQtyCodes = mc.MaterialCodes || mc.materialCodes || [];
          }
          codeAndQtyRows.push({
            minQty: mc.MinQty != null ? mc.MinQty : mc.minQty,
            maxQty: mc.MaxQty != null ? mc.MaxQty : mc.maxQty,
            uom: mc.UnitOfMeasure || mc.unitOfMeasure || null,
          });
        } else if (type === "MaterialAttributes") {
          this.materialSearch.materialAttributes.active = true;
          var attrs = mc.MaterialAttributes || mc.materialAttributes || {};
          if (attrs.ProductCategory1Codes || attrs.productCategory1Codes) {
            this.materialSearch.materialAttributes.productCategory1Codes =
              attrs.ProductCategory1Codes || attrs.productCategory1Codes || [];
          }
          if (attrs.ProductCategory2Codes || attrs.productCategory2Codes) {
            this.materialSearch.materialAttributes.productCategory2Codes =
              attrs.ProductCategory2Codes || attrs.productCategory2Codes || [];
          }
          if (attrs.ProductCategory3Codes || attrs.productCategory3Codes) {
            this.materialSearch.materialAttributes.productCategory3Codes =
              attrs.ProductCategory3Codes || attrs.productCategory3Codes || [];
          }
          if (attrs.ProductCategory4Codes || attrs.productCategory4Codes) {
            this.materialSearch.materialAttributes.productCategory4Codes =
              attrs.ProductCategory4Codes || attrs.productCategory4Codes || [];
          }
          if (attrs.ProductTypeCodes || attrs.productTypeCodes) {
            this.materialSearch.materialAttributes.productTypeCodes =
              attrs.ProductTypeCodes || attrs.productTypeCodes || [];
          }
        }
      }

      if (qtyRangeRows.length > 0) {
        this.materialSearch.qtyRange.rows = qtyRangeRows;
      }
      if (codeAndQtyRows.length > 0) {
        this.materialSearch.codeAndQty.codes = codeAndQtyCodes;
        this.materialSearch.codeAndQty.rows = codeAndQtyRows;
      }

      // Restore WO scope filters
      if (scope.WoStatuses && scope.WoStatuses.length)
        this.scopeFilters.woStatus = scope.WoStatuses[0];
      if (scope.WoTypeCode != null) this.scopeFilters.woType = scope.WoTypeCode;
      if (scope.BranchCode != null)
        this.scopeFilters.branchCode = scope.BranchCode;
      if (scope.DivisionCode != null)
        this.scopeFilters.divisionCode = scope.DivisionCode;
      if (scope.ScheduledDateFrom != null)
        this.scopeFilters.scheduledDateFrom = scope.ScheduledDateFrom;
      if (scope.ScheduledDateTo != null)
        this.scopeFilters.scheduledDateTo = scope.ScheduledDateTo;
      if (scope.ProductCode != null)
        this.scopeFilters.productCode = scope.ProductCode;
      if (scope.ProductCategory1Code != null)
        this.scopeFilters.productCategory1 = scope.ProductCategory1Code;
      if (scope.ProductCategory2Code != null)
        this.scopeFilters.productCategory2 = scope.ProductCategory2Code;
      if (scope.ProductCategory3Code != null)
        this.scopeFilters.productCategory3 = scope.ProductCategory3Code;
      if (scope.ProductCategory4Code != null)
        this.scopeFilters.productCategory4 = scope.ProductCategory4Code;
      if (scope.ProductTypeCode != null)
        this.scopeFilters.productType = scope.ProductTypeCode;

      // Restore product attribute scope filters (1–20)
      var guidsToResolve = [];
      for (var j = 1; j <= 20; j++) {
        var n = String(j).padStart(2, "0");
        var guid = scope["Attribute" + n];
        var code = scope["AttributeCode" + n];
        if (guid != null) this.attrGuidMap["productAttribute" + n] = guid;
        if (code != null) {
          this.attrScopeFilters["productAttribute" + n] = code;
        } else if (guid != null) {
          guidsToResolve.push({ n: n, guid: guid });
        }
      }

      if (guidsToResolve.length > 0) {
        await Promise.all(
          guidsToResolve.map(
            function (item) {
              return ApiService.oDataGet({
                url: "api/ProductAttribute" + item.n,
                top: "1",
                filter: "RecordId eq " + item.guid,
                select: "Code",
              })
                .then(
                  function (res) {
                    var record =
                      res.data && res.data.value && res.data.value[0];
                    if (record) {
                      this.attrScopeFilters["productAttribute" + item.n] =
                        record.Code;
                    }
                  }.bind(this),
                )
                .catch(function () {
                  // skip if attribute lookup fails
                });
            }.bind(this),
          ),
        );
      }

      this.$nextTick(
        function () {
          this.lastSearchedSnapshot = JSON.stringify(
            this.buildSearchPayload().SearchCriteria,
          );
        }.bind(this),
      );
    },
    onScopeFieldChanged(e) {
      this.scopeFilters[e.dataField] = e.value !== undefined ? e.value : null;
    },
    onAttrValueChanged(key, e) {
      this.attrScopeFilters[key] = e.value !== undefined ? e.value : null;
    },
    onAttrSelectionChanged(key, e) {
      this.attrGuidMap[key] = e.selectedItem ? e.selectedItem.RecordId : null;
    },
    resetStep1Filters() {
      this.materialSearch.codeOnly.active = false;
      this.materialSearch.codeOnly.codes = [];
      this.materialSearch.qtyRange.active = false;
      this.materialSearch.qtyRange.rows = [
        { minQty: null, maxQty: null, uom: null },
      ];
      this.materialSearch.codeAndQty.active = false;
      this.materialSearch.codeAndQty.codes = [];
      this.materialSearch.codeAndQty.rows = [
        { minQty: null, maxQty: null, uom: null },
      ];
      this.materialSearch.materialAttributes.active = false;
      this.materialSearch.materialAttributes.productCategory1Codes = [];
      this.materialSearch.materialAttributes.productCategory2Codes = [];
      this.materialSearch.materialAttributes.productCategory3Codes = [];
      this.materialSearch.materialAttributes.productCategory4Codes = [];
      this.materialSearch.materialAttributes.productTypeCodes = [];
      const keys = Object.keys(this.scopeFilters);
      keys.forEach((key) => {
        this.scopeFilters[key] = null;
      });
    },
    onMaterialCodeSelected(blockType, event) {
      if (!event.value) return;
      if (!this.materialSearch[blockType].codes.includes(event.value)) {
        this.materialSearch[blockType].codes.push(event.value);
      }
      const selectKey =
        blockType === "codeOnly"
          ? "codeOnlySelectValue"
          : "codeAndQtySelectValue";
      const refName =
        blockType === "codeOnly" ? "codeOnlySelect" : "codeAndQtySelect";
      this.$nextTick(() => {
        this[selectKey] = null;
        if (this.$refs[refName]) {
          this.$refs[refName].instance.option("value", null);
          this.$refs[refName].instance.focus();
        }
      });
    },
    removeCode(blockType, index) {
      this.materialSearch[blockType].codes.splice(index, 1);
    },
    addQtyRangeRow() {
      this.materialSearch.qtyRange.rows.push({
        minQty: null,
        maxQty: null,
        uom: null,
      });
    },
    removeQtyRangeRow(index) {
      this.materialSearch.qtyRange.rows.splice(index, 1);
    },
    addCodeAndQtyRow() {
      this.materialSearch.codeAndQty.rows.push({
        minQty: null,
        maxQty: null,
        uom: null,
      });
    },
    removeCodeAndQtyRow(index) {
      this.materialSearch.codeAndQty.rows.splice(index, 1);
    },
    onMaterialSelectFocusIn() {
      // Could load data sources on focus if needed
    },
    isValidRangeRow(row) {
      return (
        row.minQty != null &&
        row.maxQty != null &&
        !!row.uom &&
        row.maxQty >= row.minQty
      );
    },
    isValidSearch() {
      if (this.materialSearch.codeOnly.active) {
        if (this.materialSearch.codeOnly.codes.length === 0) return false;
      }
      if (this.materialSearch.qtyRange.active) {
        const valid = this.materialSearch.qtyRange.rows.every((row) =>
          this.isValidRangeRow(row),
        );
        if (!valid) return false;
      }
      if (this.materialSearch.codeAndQty.active) {
        if (this.materialSearch.codeAndQty.codes.length === 0) return false;
        const valid = this.materialSearch.codeAndQty.rows.every((row) =>
          this.isValidRangeRow(row),
        );
        if (!valid) return false;
      }
      return true;
    },
    formatDateRange(from, to) {
      if (!from && !to) return "—";
      const f = from ? new Date(from).toLocaleDateString() : "—";
      const t = to ? new Date(to).toLocaleDateString() : "—";
      return `${f} to ${t}`;
    },
    onSearch() {
      if (this.lastSearchedSnapshot && !this.hasChangedSinceSearch) {
        this.$emit("go-next");
        return;
      }
      this.executeSearch();
    },

    async executeSearch() {
      this.searchAttempted = true;

      if (!this.canSearch) return;

      this.showLoading(true);
      try {
        this.searching = true;
        const payload = this.buildSearchPayload();
        const res = await WoBulkUpdateService.search(payload);
        const matched = (res.data && res.data.MatchedWos) || [];
        if (matched.length === 0) {
          await this.$alert(
            "No Work Orders matched the search criteria. Please adjust your search and try again.",
            "No Results",
          );
          return;
        }
        this.lastSearchedSnapshot = JSON.stringify(payload.SearchCriteria);
        const sc = payload.SearchCriteria;
        this.$emit("search-complete", {
          matchedWoList: matched,
          searchCriteria: sc,
          hasMaterialCriteria: (sc.MaterialCriteria || []).length > 0,
        });
        this.$emit("criteria-change", sc);
      } catch (err) {
        this.showLoading(false);
        this.$notify("Search failed. Please try again.", "error");
      } finally {
        this.showLoading(false);
        this.searching = false;
      }
    },

    buildSearchPayload() {
      const MaterialCriteria = [];
      const m = this.materialSearch;
      var self = this;

      if (m.codeOnly.active && m.codeOnly.codes.length > 0) {
        MaterialCriteria.push({
          SearchType: "CodeOnly",
          MaterialCodes: m.codeOnly.codes,
        });
      }
      if (m.qtyRange.active) {
        m.qtyRange.rows.forEach(function (row) {
          if (self.isValidRangeRow(row)) {
            MaterialCriteria.push({
              SearchType: "QtyRangeOnly",
              MinQty: row.minQty,
              MaxQty: row.maxQty,
              UnitOfMeasure: row.uom,
            });
          }
        });
      }
      if (m.codeAndQty.active && m.codeAndQty.codes.length > 0) {
        m.codeAndQty.rows.forEach(function (row) {
          if (self.isValidRangeRow(row)) {
            MaterialCriteria.push({
              SearchType: "CodeAndQty",
              MaterialCodes: m.codeAndQty.codes,
              MinQty: row.minQty,
              MaxQty: row.maxQty,
              UnitOfMeasure: row.uom,
            });
          }
        });
      }
      if (m.materialAttributes.active) {
        var a = m.materialAttributes;
        var attrFilter = {};
        if (a.productCategory1Codes && a.productCategory1Codes.length)
          attrFilter.ProductCategory1Codes = a.productCategory1Codes;
        if (a.productCategory2Codes && a.productCategory2Codes.length)
          attrFilter.ProductCategory2Codes = a.productCategory2Codes;
        if (a.productCategory3Codes && a.productCategory3Codes.length)
          attrFilter.ProductCategory3Codes = a.productCategory3Codes;
        if (a.productCategory4Codes && a.productCategory4Codes.length)
          attrFilter.ProductCategory4Codes = a.productCategory4Codes;
        if (a.productTypeCodes && a.productTypeCodes.length)
          attrFilter.ProductTypeCodes = a.productTypeCodes;
        MaterialCriteria.push({
          SearchType: "MaterialAttributes",
          MaterialAttributes: attrFilter,
        });
      }

      const s = this.scopeFilters;
      const ScopeFilters = {};
      if (s.woStatus != null) ScopeFilters.WoStatuses = [s.woStatus];
      if (s.woType != null) ScopeFilters.WoTypeCode = s.woType;
      if (s.branchCode != null) ScopeFilters.BranchCode = s.branchCode;
      if (s.divisionCode != null) ScopeFilters.DivisionCode = s.divisionCode;
      if (s.scheduledDateFrom)
        ScopeFilters.ScheduledDateFrom = s.scheduledDateFrom;
      if (s.scheduledDateTo) ScopeFilters.ScheduledDateTo = s.scheduledDateTo;
      if (s.productCode != null) ScopeFilters.ProductCode = s.productCode;
      if (s.productCategory1 != null)
        ScopeFilters.ProductCategory1Code = s.productCategory1;
      if (s.productCategory2 != null)
        ScopeFilters.ProductCategory2Code = s.productCategory2;
      if (s.productCategory3 != null)
        ScopeFilters.ProductCategory3Code = s.productCategory3;
      if (s.productCategory4 != null)
        ScopeFilters.ProductCategory4Code = s.productCategory4;
      if (s.productType != null) ScopeFilters.ProductTypeCode = s.productType;
      for (var i = 1; i <= 20; i++) {
        var stateKey = "productAttribute" + String(i).padStart(2, "0");
        var n = String(i).padStart(2, "0");
        var guid = this.attrGuidMap[stateKey];
        if (guid != null) ScopeFilters["Attribute" + n] = guid;
      }

      return {
        SearchCriteria: { MaterialCriteria, ScopeFilters },
      };
    },
  },
};
</script>

<style lang="scss">
.material-search-dropdown {
  .dx-list-item-content::before {
    content: unset !important;
  }

  .dx-list-item-content {
    font-size: 13px;
    padding: 10px 11px 9px;
  }
}

.gc-wo-bulk-update-step-search {
  .search-page-content {
    display: flex;
    gap: 10px;
    align-items: flex-start;
  }

  .search-main-col {
    flex: 1;
    min-width: 0;
  }

  .search-filter-col {
    width: 300px;
    flex-shrink: 0;

    .search-card-body {
      padding: 16px !important;
    }
  }

  .filters-readonly .chip-x {
    display: none;
  }

  // ── Divider / scope section ───────────────────────────────────────────────
  .search-divider {
    height: 1px;
    background: #eaecf0;
    margin: 14px 0;
  }

  .search-card {
    background: #fff;
    border: 1px solid #d0d5dd;
  }

  .search-card-header {
    padding: 12px 16px;
    border-bottom: 1px solid #eaecf0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .search-card-title {
    font-size: 14px;
    font-weight: 700;
    color: #101828;
  }

  .search-card-body {
    padding: 16px;
    padding-top: unset !important;
  }

  .search-hint-text {
    font-size: 13px;
    color: #98a2b3;
    margin-bottom: 14px;
    line-height: 1.5;
  }

  .stb {
    border: 1px solid #d0d5dd;
    padding: 12px;
    margin-bottom: 8px;
    background: #f9fafb;
    transition: border-color 0.12s, background 0.12s;

    &.stb-on {
      border-color: #03a9f4;
      background: #f0faff;
    }

    .stb-hdr {
      display: flex;
      align-items: center;
      gap: 8px;

      .stb-chk {
        width: 16px;
        height: 16px;
        cursor: pointer;
        flex-shrink: 0;
      }

      .stb-lbl {
        font-size: 14px;
        font-weight: 500;
        color: #101828;
        cursor: pointer;
        user-select: none;
      }
    }

    .stb-desc {
      font-size: 12px;
      color: #98a2b3;
      margin-left: 24px;
      margin-top: 3px;
    }

    .stb-body {
      padding-left: 24px;
      margin-top: 10px;
    }

    .tag-list {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-top: 8px;
    }

    .stb-tag {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      background: #fff;
      border: 1px solid #d0d5dd;
      border-radius: 6px;
      padding: 2px 4px 2px 9px;
      font-size: 13px;
      font-weight: 500;
      color: #344054;
    }

    .stb-tag-x {
      cursor: pointer;
      font-size: 12px;
      color: #98a2b3;
      padding: 2px;
      line-height: 1;

      &:hover {
        color: #101828;
      }
    }

    .stb-error {
      font-size: 12px;
      color: #d92d20;
      margin-top: 6px;
      display: block;
    }
  }

  .replacement-header {
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
    font-weight: 500;
    font-size: 12px;
    color: #344054;

    .rt-col-code {
      width: 125px;
      font-size: 11px;
      font-weight: 600;
      color: #6c757d;
      text-transform: uppercase;
      letter-spacing: 0.4px;
    }

    .rt-col-del {
      width: 28px;
    }
  }

  .replacement-row {
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
    align-items: flex-start;
  }

  .rt-field {
    width: 125px;
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 2px;

    .dx-selectbox,
    .dx-numberbox {
      width: 100%;
    }

    &.rt-field-narrow {
      flex: 0 0 120px;
    }

    .param-error {
      display: block;
      color: #d92d20;
      font-size: 11px;
      margin-top: 2px;
    }
  }

  .rt-col-del {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .rt-del-btn {
    padding: 4px !important;
  }

  .add-row-btn {
    margin-top: 8px;
  }

  .attribute-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px 12px;
  }

  .attribute-field {
    display: flex;
    flex-direction: column;
    gap: 6px;

    label {
      font-size: 13px;
      font-weight: 500;
      color: #344054;
      display: block;
    }

    // DxTagBox styling
    .dx-tagbox {
      width: 100%;

      .dx-textbox-container {
        border: 1px solid #d0d5dd;
        border-radius: 4px;
        background: #fff;
        min-height: 32px;
        padding: 4px 8px;

        &:hover {
          border-color: #98a2b3;
        }

        &.dx-state-focused {
          border-color: #03a9f4;
          box-shadow: 0 0 0 2px rgba(3, 169, 244, 0.1);
        }
      }

      .dx-tag {
        background: #ffffff;
        border: 1px solid #d0d5dd;
        color: #000000;
        border-radius: 4px;
        padding: 3px 6px;
        font-size: 12px;
        margin: 2px 2px;

        .dx-tag-content {
          background-color: white !important;

          > span {
            font-weight: 500;
            color: black !important;
          }
        }

        &:hover {
          background: #e7eef7;
        }

        .dx-tag-remove-button {
          color: #98a2b3;
          margin-left: 4px;

          &:hover {
            color: #d92d20;
          }

          cursor: pointer;
          font-size: 12px;
          color: #98a2b3;
          padding: 2px;
          line-height: 1;
        }
      }

      .dx-texteditor-input {
        font-size: 13px;
        color: #344054;

        &::placeholder {
          color: #98a2b3;
        }
      }

      .dx-placeholder {
        color: #98a2b3;
      }
    }
  }

  .scope-filters-section {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid #eaecf0;

    h3 {
      font-size: 12px;
      font-weight: 500;
      color: #98a2b3;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      margin: 0 0 10px 0;
    }
  }

  .scope-filters-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 8px 12px;
  }

  .filter-group {
    display: flex;
    flex-direction: column;
    gap: 6px;

    label {
      font-weight: 500;
      font-size: 12px;
      color: #344054;
    }
  }

  .search-action-row {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 10px;
    margin-top: 16px;

    .search-error-hint {
      font-size: 12px;
      color: #d92d20;
    }
  }

  .custom-auto-complete {
    display: flex;
    flex-direction: column;

    .auto-complete-primary {
      font-weight: 500;
      color: #344054;
    }

    .auto-complete-secondary {
      font-size: 12px;
      color: #98a2b3;
    }
  }

  .scope-section-hdr {
    font-size: 12px;
    font-weight: 500;
    color: #98a2b3;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 10px;
  }

  .scope-attrs-toggle {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 7px 0;
    font-size: 13px;
    font-weight: 500;
    color: #344054;
    cursor: pointer;
    user-select: none;
    border-top: 1px solid #eaecf0;
    margin-bottom: 0;

    &:hover {
      color: #101828;
    }
  }

  .scope-attrs-caret {
    margin-left: auto;
    font-size: 11px;
    color: #98a2b3;
  }

  .scope-attrs-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9px;
    background: #03a9f4;
    color: #fff;
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
  }

  .scope-attrs-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px 12px;
    margin-top: 8px;
    margin-bottom: 12px;
  }

  .scope-attr-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .filter-section-label {
    font-size: 11px;
    font-weight: 500;
    color: #98a2b3;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-top: 14px;
    margin-bottom: 6px;
    display: block;

    &:first-child {
      margin-top: 0;
    }
  }

  .filter-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: #fff;
    border: 1px solid #d0d5dd;
    border-radius: 6px;
    padding: 2px 4px 2px 9px;
    font-size: 13px;
    font-weight: 500;
    color: #344054;
    margin: 3px 3px 0 0;
  }

  .chip-x {
    cursor: pointer;
    font-size: 12px;
    color: #98a2b3;
    padding: 2px;

    &:hover {
      color: #101828;
    }
  }

  .search-no-filters {
    font-size: 13px;
    color: #98a2b3;
  }
}
</style>
